import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useStartSwachhataStudentQuizMutation,
  useGetSwachhataStudentAttemptMutation,
  useSubmitSwachhataStudentQuizMutation,
} from "../redux/auth/authApi";
import { SLIDES, CAMPAIGN, BINS } from "../Components/SchoolAdmin/Swachhata/swachhataContent";
import SwachhataDeck from "../Components/SchoolAdmin/Swachhata/SwachhataDeck";
import SwachhataQuiz from "../Components/SchoolAdmin/Swachhata/SwachhataQuiz";
import SwachhataStudentForm from "../Components/SchoolAdmin/Swachhata/SwachhataStudentForm";
import { ResultView, ReviewView } from "../Components/SchoolAdmin/Swachhata/SwachhataResult";
import SwachhataCertificatePanel from "../Components/SchoolAdmin/Swachhata/SwachhataCertificate";
import { Leaves, Scene } from "../Components/SchoolAdmin/Swachhata/SwachhataBits";
import SwachhataSideArt from "../Components/SchoolAdmin/Swachhata/SwachhataSideArt";
import ecoLogo from "../assets/images/Logo-2.png";
import { SWACHHATA_QUIZ_OPEN } from "../config/campaigns";
import "../Components/SchoolAdmin/Swachhata/Swachhata.css";

/*
 * /swachhata-hi-seva — PUBLIC student page (no login), reached from the
 * landing page.
 *
 *   welcome → learn (optional) → register (name, class, age, UDISE looked up
 *   on UDISE+) → quiz → result → certificate (score ≥ 3) / review
 *
 * Built for lakhs of students: starting the quiz returns a SIGNED PAPER and
 * writes nothing; the paper, the questions and every answer stay in this
 * browser (localStorage) until submit, so answering, reloading or resuming
 * never touches the server. Submit sends the paper + answers once; the
 * server grades, stores one record and — at 3/10 or more — issues the
 * certificate, which opens here and downloads straight away.
 */

const LAST = SLIDES.length - 1;
const PROGRESS_KEY = "shs_public_progress";
const PAPER_KEY = "shs_public_paper_v2"; // { paper, attemptId, questions, startedAt, student, school, udise_id }
const DRAFT_KEY = "shs_public_draft";
const RESULT_KEY = "shs_public_result"; // { attemptId } of the last submitted attempt
const LEGACY_ATTEMPT_KEY = "shs_public_attempt"; // attempts started before signed papers
const loadJson = (k, fb) => { try { return JSON.parse(localStorage.getItem(k) || "") ?? fb; } catch { return fb; } };
const saveJson = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* ignore */ } };
const clearKey = (k) => { try { localStorage.removeItem(k); } catch { /* ignore */ } };
const clamp = (n) => Math.max(0, Math.min(LAST, Math.trunc(Number(n) || 0)));
const errMsg = (e, fb) => e?.data?.message || e?.error?.data?.message || e?.message || fb;
const PAPER_MAX_AGE_MS = 6 * 60 * 60 * 1000;

export default function SwachhataPublic() {
  const navigate = useNavigate();
  const startView = new URLSearchParams(useLocation().search).get("start");
  const saved = useMemo(() => loadJson(PROGRESS_KEY, { last: 0, max: 0 }), []);
  // A paper in progress resumes with no server call (unless it is too old to submit).
  const savedPaper = useMemo(() => {
    const p = loadJson(PAPER_KEY, null);
    if (!p?.paper || !Array.isArray(p.questions) || !p.questions.length) return null;
    if (p.startedAt && Date.now() - new Date(p.startedAt).getTime() > PAPER_MAX_AGE_MS - 5 * 60 * 1000) {
      clearKey(PAPER_KEY); clearKey(DRAFT_KEY);
      return null;
    }
    return p;
  }, []);
  const savedResult = useMemo(() => (savedPaper ? null : loadJson(RESULT_KEY, null) || loadJson(LEGACY_ATTEMPT_KEY, null)), [savedPaper]);

  // Quiz closed (config/campaigns.js): a paper left in progress cannot be
  // submitted any more, so nothing opens the quiz or the details form.
  const initialPhase =
    startView === "learn" ? "learn"
      : !SWACHHATA_QUIZ_OPEN ? "welcome"
        : savedPaper ? "quiz" : startView === "quiz" ? "register" : "welcome";
  const [phase, setPhase] = useState(initialPhase);
  const [slide, setSlide] = useState(() => (startView === "learn" && saved.max > 0 ? clamp(Math.min(saved.max, saved.last)) : 0));
  const [dir, setDir] = useState("fwd");
  const [maxVisited, setMaxVisited] = useState(clamp(saved.max));
  const [paper, setPaper] = useState(savedPaper);
  const [legacyAttemptId, setLegacyAttemptId] = useState(null);
  const [questions, setQuestions] = useState(savedPaper?.questions || []);
  const [answers, setAnswers] = useState(() => {
    if (!savedPaper) return {};
    const draft = loadJson(DRAFT_KEY, {});
    const valid = {};
    savedPaper.questions.forEach((q) => { if (draft[q.id]) valid[q.id] = draft[q.id]; });
    return valid;
  });
  const [attempt, setAttempt] = useState(null);
  const [lastStudent, setLastStudent] = useState(savedPaper ? { ...savedPaper.student, udise_id: savedPaper.udise_id } : null);
  const [fresh, setFresh] = useState(false);
  const [autoCert, setAutoCert] = useState(false);
  const [err, setErr] = useState("");
  const [resumeChecked, setResumeChecked] = useState(!savedResult?.attemptId);
  const scrollRef = useRef(null);

  const [startQuiz, { isLoading: starting }] = useStartSwachhataStudentQuizMutation();
  const [getAttempt] = useGetSwachhataStudentAttemptMutation();
  const [submitQuiz, { isLoading: submitting }] = useSubmitSwachhataStudentQuizMutation();

  const resumeLast = clamp(Math.min(saved.max, saved.last));
  const canResume = saved.max > 0;

  useEffect(() => { document.title = `${CAMPAIGN.name} · ${CAMPAIGN.tagline} — ${CAMPAIGN.programme}`; }, []);
  useEffect(() => { window.scrollTo({ top: 0 }); }, [phase]);

  // Only a SUBMITTED attempt is ever fetched back (one indexed read), e.g. to
  // download the certificate again after closing the tab.
  useEffect(() => {
    if (!savedResult?.attemptId) return;
    (async () => {
      try {
        const res = await getAttempt({ attemptId: savedResult.attemptId }).unwrap();
        const a = res.attempt;
        if (a?.quiz?.status === "submitted") {
          setAttempt(a);
          if (startView !== "learn" && startView !== "quiz") setPhase("result");
        } else if (res.questions?.length) {
          // Started before signed papers: finish it on the old path.
          setLegacyAttemptId(a.attemptId);
          setQuestions(res.questions);
          setLastStudent({ ...a.student, udise_id: a.udise_id });
          const draft = loadJson(DRAFT_KEY, {});
          const valid = {};
          res.questions.forEach((q) => { if (draft[q.id]) valid[q.id] = draft[q.id]; });
          setAnswers(valid);
          if (startView !== "learn" && SWACHHATA_QUIZ_OPEN) setPhase("quiz");
        }
      } catch {
        clearKey(RESULT_KEY); clearKey(LEGACY_ATTEMPT_KEY);
      } finally {
        setResumeChecked(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goSlide = useCallback((n) => {
    if (n < 0 || n >= SLIDES.length) return;
    setDir(n > slide ? "fwd" : "back");
    setSlide(n);
    setMaxVisited((m) => {
      const max = Math.max(m, n);
      saveJson(PROGRESS_KEY, { last: n, max });
      return max;
    });
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [slide]);

  useEffect(() => {
    if (phase !== "learn") return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowRight") goSlide(slide + 1);
      if (e.key === "ArrowLeft") goSlide(slide - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, slide, goSlide]);

  const onStart = async (student) => {
    setErr("");
    try {
      const res = await startQuiz(student).unwrap();
      const p = {
        paper: res.paper,
        attemptId: res.attemptId,
        questions: res.questions || [],
        startedAt: res.startedAt,
        student: res.student,
        school: res.school,
        udise_id: res.udise_id,
      };
      saveJson(PAPER_KEY, p);
      clearKey(DRAFT_KEY); clearKey(LEGACY_ATTEMPT_KEY);
      setPaper(p);
      setLegacyAttemptId(null);
      setQuestions(p.questions);
      setAnswers({});
      setAttempt(null);
      setLastStudent({ ...res.student, udise_id: res.udise_id });
      setPhase("quiz");
    } catch (e) {
      setErr(errMsg(e, "Could not start the quiz. Please try again."));
    }
  };

  // Answers live only in this browser until submit.
  const onAnswer = useCallback((qid, key) => {
    setAnswers((a) => { const n = { ...a, [qid]: key }; saveJson(DRAFT_KEY, n); return n; });
  }, []);

  const showResult = (a) => {
    setAttempt(a);
    setFresh(true);
    saveJson(RESULT_KEY, { attemptId: a.attemptId });
    // Always land on the result first (score, badge, answers); the certificate
    // opens only when the student taps "View / download my certificate".
    setAutoCert(false);
    setPhase("result");
  };

  const doSubmit = async () => {
    setErr("");
    try {
      const body = paper ? { paper: paper.paper, answers } : { attemptId: legacyAttemptId, answers };
      const res = await submitQuiz(body).unwrap();
      clearKey(PAPER_KEY); clearKey(DRAFT_KEY); clearKey(LEGACY_ATTEMPT_KEY);
      setPaper(null);
      setLegacyAttemptId(null);
      showResult(res.attempt);
    } catch (e) {
      const code = e?.data?.code;
      if (code === "ALREADY_SUBMITTED" && e?.data?.attempt) { clearKey(PAPER_KEY); clearKey(DRAFT_KEY); showResult(e.data.attempt); return; }
      // PAPER_INVALID: expired paper. UDISE_NOT_ALLOWED: a paper started on the
      // internal test UDISE before it was blocked — neither can be stored.
      if (code === "PAPER_INVALID" || code === "UDISE_NOT_ALLOWED") {
        clearKey(PAPER_KEY); clearKey(DRAFT_KEY);
        setPaper(null); setQuestions([]); setAnswers({});
        setPhase("register");
        setErr(errMsg(e, "This quiz paper has expired. Please start again."));
        return;
      }
      setErr(errMsg(e, "Could not submit the quiz. Please check your connection and try again — your answers are saved on this device."));
    }
  };

  const exitPage = () => navigate("/");

  // Try again, or another student on the same device.
  const startOver = () => {
    clearKey(PAPER_KEY); clearKey(DRAFT_KEY); clearKey(RESULT_KEY); clearKey(LEGACY_ATTEMPT_KEY);
    setPaper(null); setLegacyAttemptId(null); setAttempt(null); setQuestions([]); setAnswers({});
    setFresh(false); setAutoCert(false); setErr("");
    setPhase("register");
  };

  const shellClass =
    phase === "welcome" || phase === "register" ? "shs-shell shs-shell--welcome"
      : phase === "quiz" ? "shs-shell shs-shell--quiz"
        : phase === "result" ? "shs-shell shs-shell--result"
          : phase === "certificate" ? "shs-shell shs-shell--cert"
            : "shs-shell";

  const studentLine = attempt
    ? `${attempt.student?.name || ""}${attempt.student?.class ? ` · Class ${attempt.student.class}` : ""}${attempt.school?.school_name ? ` · ${attempt.school.school_name}` : attempt.udise_id ? ` · UDISE ${attempt.udise_id}` : ""}`
    : "";
  const subtitle = `${CAMPAIGN.tagline} · ${CAMPAIGN.rulesShort}`;
  const minScore = attempt?.certificateMinScore ?? 3;

  return (
    <div className={`shs-page shs-page--${phase}`}>
      <Leaves />
      {resumeChecked && ["welcome", "register", "quiz", "result"].includes(phase) && (
        <SwachhataSideArt phase={phase} answered={Object.keys(answers).length} total={questions.length || 10} />
      )}
      <div className={shellClass}>
        {!resumeChecked && (
          <div className="shs-loading"><span className="shs-spinner" />Loading your result…</div>
        )}

        {/* ── Welcome ─────────────────────────────────────────────────────── */}
        {resumeChecked && phase === "welcome" && (
          <div className="shs-welcome">
            <div>
              <span className="shs-welcome__logo"><img src={ecoLogo} alt={CAMPAIGN.programme} /></span>
              <h1 className="shs-welcome__title">{CAMPAIGN.name}</h1>
              <div className="shs-welcome__sub">{subtitle}</div>
              <div className="shs-welcome__slogan">{CAMPAIGN.slogan}</div>
              <p className="shs-welcome__lead">
                {SWACHHATA_QUIZ_OPEN
                  ? "Study the learning material on where our waste goes and the four bins, or start the 10-question quiz straight away. No login is needed. You will be asked for your name, class, age and your school's UDISE code before the quiz. Score 3 or more to receive your participation certificate."
                  : "The Swachhata Hi Seva 2026 quiz is now closed. Thank you to every student who took part. The learning material on where our waste goes and the four bins is still here to read."}
              </p>
              {canResume && (
                <div className="shs-resume"><span>{`Progress saved on this device — you were on page ${resumeLast + 1} of ${SLIDES.length} (${SLIDES[resumeLast].rail})`}</span></div>
              )}
              <div className="shs-welcome__actions">
                <button type="button" className="shs-btn shs-btn--primary shs-btn--lg" onClick={() => { setPhase("learn"); goSlide(canResume ? resumeLast : 0); }}>
                  Study the material <span className="shs-btn__arrow">→</span>
                </button>
                {SWACHHATA_QUIZ_OPEN && (
                  <button type="button" className="shs-btn shs-btn--orange shs-btn--lg shs-btn--pulse" onClick={() => setPhase(paper ? "quiz" : "register")}>
                    {paper ? "Continue the quiz" : "Start the quiz"} <span className="shs-btn__arrow">→</span>
                  </button>
                )}
              </div>
              <div className="shs-welcome__meta">{CAMPAIGN.ministry}</div>
            </div>
            <div className="shs-welcome__scene"><Scene bins={BINS} /></div>
          </div>
        )}

        {/* ── Learn ───────────────────────────────────────────────────────── */}
        {resumeChecked && phase === "learn" && (
          <SwachhataDeck
            slide={slide}
            maxVisited={maxVisited}
            dir={dir}
            scrollRef={scrollRef}
            onGo={goSlide}
            onBack={() => setPhase("welcome")}
            onFinish={() => setPhase(!SWACHHATA_QUIZ_OPEN ? "welcome" : paper ? "quiz" : "register")}
            onExit={exitPage}
            finishLabel={SWACHHATA_QUIZ_OPEN ? "Take the quiz →" : "Finish"}
            finishTone={SWACHHATA_QUIZ_OPEN ? "orange" : "primary"}
            logo={ecoLogo}
            audience="student"
            unlockAll
          />
        )}

        {/* ── Register ────────────────────────────────────────────────────── */}
        {resumeChecked && phase === "register" && (
          <div className="shs-intro">
            <span className="shs-badge-pill"><img src={ecoLogo} alt="" />{`Student quiz · ${CAMPAIGN.name} · ${CAMPAIGN.slogan}`}</span>
            <h2 className="shs-slide__title" style={{ marginTop: 14 }}>Tell us who is taking the quiz</h2>
            <p className="shs-slide__lead">Your name, class, age and your school's UDISE code — so your participation counts for your school in the campaign report.</p>
            <SwachhataStudentForm onStart={onStart} starting={starting} serverError={err} initial={lastStudent || undefined} />
            <div className="shs-intro__actions" style={{ marginTop: 10 }}>
              <button type="button" className="shs-btn shs-btn--ghost shs-btn--sm" onClick={() => setPhase("welcome")}>← Back</button>
            </div>
          </div>
        )}

        {/* ── Quiz ────────────────────────────────────────────────────────── */}
        {resumeChecked && phase === "quiz" && questions.length > 0 && (
          <SwachhataQuiz
            questions={questions}
            answers={answers}
            onAnswer={onAnswer}
            onSubmit={doSubmit}
            submitting={submitting}
            startedAt={paper?.startedAt}
            error={err}
            onExit={exitPage}
          />
        )}

        {/* ── Result ──────────────────────────────────────────────────────── */}
        {resumeChecked && phase === "result" && attempt && (
          <ResultView
            participation={attempt}
            certificateMinScore={minScore}
            fresh={fresh}
            subtitle={studentLine}
            resultTitle="Your result"
            onReview={() => setPhase("review")}
            onReadAgain={() => { setPhase("learn"); goSlide(0); }}
            onCertificate={attempt.certificate?.id ? () => { setAutoCert(false); setPhase("certificate"); } : undefined}
            certificateLabel="🎓 View / download my certificate"
            onRetry={SWACHHATA_QUIZ_OPEN ? startOver : undefined}
            onClose={startOver}
            closeLabel="Another student? Start again"
          />
        )}

        {/* ── Certificate (opens straight after a successful submit) ────────── */}
        {resumeChecked && phase === "certificate" && attempt?.certificate?.id && (
          <SwachhataCertificatePanel
            kind="student"
            participation={attempt}
            celebrate={fresh}
            compact
            autoDownload={autoCert ? "pdf" : false}
            onBack={() => setPhase("result")}
            backLabel="See my score & correct answers"
            onExit={exitPage}
          />
        )}

        {/* ── Review ──────────────────────────────────────────────────────── */}
        {resumeChecked && phase === "review" && attempt && (
          <ReviewView review={attempt.quiz?.review || []} onBack={() => setPhase("result")} onClose={() => navigate("/")} closeLabel="Back to home" />
        )}
      </div>
    </div>
  );
}
